import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { studentsAPI } from '../../services/studentService';
import { classesAPI } from '../../services/classService';
import { feesAPI } from '../../services/feesService';
import StudentTable from './StudentTable';
import StudentForm from './StudentForm';
import StudentDetails from './StudentDetails';
import FeeDetails from './FeeDetails';
import PayFeeDialog from './PayFeeDialog';
import StudentFilter from './StudentFilter';
import PaymentInvoice from './PaymentInvoice';
import TransportForm from './TransportForm';
import { transportAPI } from '../../services/transportService';

const Students = () => {
  const { user } = useAuth();
  const toast = useRef(null);

  // data
  const [students, setStudents] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);

  // filters and UI state
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active');

  // lazy params (server-side)
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 4,
    page: 1,
    sortField: null,
    sortOrder: null
  });

  // dialogs and details
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editingTransport, setEditingTransport] = useState(null);
  const [studentTransportForm, setStudentTransportForm] = useState(null);


  // student form state
  const [studentForm, setStudentForm] = useState({
    id: '',
    photoUrl: '',
    admissionNumber: '',
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    parentAddress: '',
    bloodGroup: '',
    gender: '',
    status: 'active',
    admissionDate: null,
    classId: '',
    sectionId: ''
  });
  const [transportForm, setTansportForm] = useState({
    id: '',
    transportSlabId: '',
  });

  // details view replaces table when true
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [detailsStudent, setDetailsStudent] = useState(null);
  const [showTransportForm, setShowTransportForm] = useState(false);

  // fee details view 
  const [showFeeDetails, setShowFeeDetails] = useState(false);
  const [feeDetailsStudent, setFeeDetailsStudent] = useState(null);
  const [studentFees, setStudentFees] = useState([]);
  const [feeLoading, setFeeLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(null);

  // pay fee dialog
  const [showPayFeeDialog, setShowPayFeeDialog] = useState(false);
  const [payingFee, setPayingFee] = useState(null);
  const [payableAmount, setPayableAmount] = useState(0);
  const [payingAmount, setPayingAmount] = useState(0);
  const [originalPayableAmount, setOriginalPayableAmount] = useState(0);
  const [couponVerified, setCouponVerified] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash');

  const invoiceRef = useRef();
  const [paymentHistoryId, setPaymentHistoryId] = useState(null);

  // Helper function to convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // status options
  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Graduated', value: 'graduated' },
    { label: 'Transferred', value: 'transferred' },
    { label: 'All Status', value: '' }
  ];

  useEffect(() => {
    loadClasses();
  }, [])

  useEffect(() => {
    loadStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyParams, selectedClass, selectedSection, selectedStatus, globalFilterValue]);

  // --- API loaders ---
  const loadClasses = async () => {
    try {
      const res = await classesAPI.getAllclasses();
      if (res?.data?.success) setClasses(res.data.data || []);
      else setClasses([]);
    } catch (err) {
      console.error('loadClasses error', err);
      setClasses([]);
    }
  };

  const loadSections = async (classId) => {
    if (!classId) {
      setSections([]);
      return;
    }
    try {
      const res = await classesAPI.getsectionsbyclass(classId);
      if (res?.data?.success) setSections(res.data.data || []);
      else setSections([]);
    } catch (err) {
      console.error('loadSections error', err);
      setSections([]);
    }
  };

  const buildQueryParams = () => {
    const params = {
      page: lazyParams.page || Math.floor(lazyParams.first / Math.max(1, lazyParams.rows)) + 1,
      limit: lazyParams.rows || 10,
      search: globalFilterValue || '',
      classId: selectedClass || '',
      sectionId: selectedSection || '',
      status: selectedStatus || ''
    };

    if (lazyParams.sortField) {
      params.sortField = lazyParams.sortField;
      params.sortOrder = lazyParams.sortOrder === 1 ? 'asc' : 'desc';
    }

    return params;
  };

  const loadStudents = async () => {
    setLoading(true);
    try {
      const params = buildQueryParams();
      if (params.page && isNaN(Number(params.page))) params.page = 1;
      if (params.limit && isNaN(Number(params.limit))) params.limit = 10;

      const res = await studentsAPI.getStudetsByParams(params);
      if (res?.data?.success) {
        const data = res.data.data || {};
        setStudents(data.students || []);
        setTotalRecords(data.pagination?.totalRows);
      } else {
        setStudents([]);
        setTotalRecords(0);
      }
    } catch (err) {
      console.error('loadStudents error', err);
      setStudents([]);
      setTotalRecords(0);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to load students' });
    } finally {
      setLoading(false);
    }
  };

  // --- DataTable event handlers ---
  const onPage = (event) => {
    setLazyParams(prev => ({ ...prev, first: event.first, rows: event.rows, page: Math.floor(event.first / event.rows) + 1 }));
  };

  const onSort = (event) => {
    setLazyParams(prev => ({ ...prev, sortField: event.sortField, sortOrder: event.sortOrder }));
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value || '';
    setGlobalFilterValue(value);
    setLazyParams(prev => ({ ...prev, first: 0, page: 1 }));
  };

  const onClassFilterChange = (e) => {
    const value = e.value || '';
    setSelectedClass(value);
    setSelectedSection('');
    loadSections(value);
    setLazyParams(prev => ({ ...prev, first: 0, page: 1 }));
  };

  const onSectionFilterChange = (e) => {
    const value = e.value || '';
    setSelectedSection(value);
    setLazyParams(prev => ({ ...prev, first: 0, page: 1 }));
  };

  const onStatusFilterChange = (e) => {
    const value = e.value ?? '';
    setSelectedStatus(value);
    setLazyParams(prev => ({ ...prev, first: 0, page: 1 }));
  };

  // --- CRUD handlers ---
  const handleAddStudent = () => {
    setEditingStudent(null);
    setStudentForm({
      id: '', photoUrl: '', admissionNumber: '', firstName: '', lastName: '',
      dateOfBirth: null, address: '', bloodGroup: '', gender: '', status: 'active',
      admissionDate: null, classId: '', sectionId: ''
    });
    setShowStudentForm(true);
  };

  const [slabs, setSlabs] = useState(null);
  const loadSlabs = async () => {
    const res = await transportAPI.getAllTransportSlabs()
    setSlabs(res?.data?.data || []);
  }
  const handleAddTransport = async (student) => {
    await loadSlabs();
    setTansportForm(student);
    setShowTransportForm(true);
  };
  const handleSaveTransport = async () => {
    try {
      setLoading(true);
      if (editingTransport) {
        await transportAPI.updateTransport(editingTransport.id, transportForm);
        toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Transport updated' });
      } else {
        await transportAPI.addTransport(transportForm.id, transportForm.transportSlabId);
        toast.current?.show({ severity: 'success', summary: 'Created', detail: 'Transport created' });
      }
      setShowTransportForm(false);
    } catch (err) {
      console.error('save transport error', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Failed to save transport' });
    } finally {
      setLoading(false);
      setShowTransportForm(false);
      setShowFeeDetails(true);
      setFeeDetailsStudent (true)
        
    }
  };
  const handleEditTransportForm = (student) => {
    setShowTransportForm(true);
    setEditingTransport(student);
    setTansportForm({
      id: '',
      transportSlabId: '',
    });
    setShowTransportForm(true);

  };
  const handleEditStudent = (student) => {
    setShowStudentForm(true);
    setEditingStudent(student);
    setStudentForm({
      id: student.id || '',
      photoUrl: student.photoUrl || '',
      admissionNumber: student.admissionNumber || '',
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      dateOfBirth: student.dateOfBirth ? new Date(student.dateOfBirth) : null,
      parentAddress: student.parentAddress || '',
      bloodGroup: student.bloodGroup || '',
      gender: student.gender || '',
      status: student.status || 'active',
      admissionDate: student.admissionDate ? new Date(student.admissionDate) : null,
      classId: student.classId || '',
      sectionId: student.sectionId || ''
    });
    if (student.classId) loadSections(student.classId);
    setShowStudentForm(true);

  };

  const handleDeleteStudent = (student) => {
    confirmDialog({
      message: `Delete ${student.firstName} ${student.lastName}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await studentsAPI.deleteStudent(student.id);
          toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Student deleted' });
          loadStudents();
        } catch (err) {
          console.error('delete error', err);
          toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Failed to delete' });
        }
      },
      acceptClassName: 'bg-red-600 hover:bg-red-700 text-white font-semibold px-6 mx-5 py-2 rounded-lg transition-all shadow-md',
      rejectClassName: 'bg-gray-400 hover:bg-gray-500 text-white font-semibold px-6 py-2 rounded-lg transition-all shadow-md',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel'
    });
  };

  const handleSaveStudent = async () => {
    try {
      setLoading(true);
      if (editingStudent) {
        await studentsAPI.updateStudent(editingStudent.id, studentForm);
        toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Student updated' });
      } else {
        await studentsAPI.addStudent(studentForm);
        toast.current?.show({ severity: 'success', summary: 'Created', detail: 'Student created' });
      }
      setShowStudentForm(false);
      setEditingStudent(null);
      loadStudents();
    } catch (err) {
      console.error('save student error', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Failed to save student' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudent = (student) => {
    setDetailsStudent(student);
    setShowDetailsView(true);
  };

  const handleBackToTable = () => {
    setShowDetailsView(false);
    setDetailsStudent(null);
  };
  const handleCloseTransport = (student) => {
    setShowTransportForm(false);
    setDetailsStudent(null);
  };

  const handleViewFeeDetails = async (student) => {
    setFeeLoading(true);
    setFeeDetailsStudent(student);
    try {
      const res = await feesAPI.getStudentFees(student.id);
      if (res?.data?.success) {
        setStudentFees(res.data.data || []);
      } else {
        setStudentFees([]);
      }
    } catch (err) {
      console.error('fetch student fees error', err);
      setStudentFees([]);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to load fee details' });
    } finally {
      setFeeLoading(false);
      setShowFeeDetails(true);
    }
  };

  const handleBackToStudentsTable = () => {
    setShowFeeDetails(false);
    setFeeDetailsStudent(null);
    setStudentFees([]);
  };

  const handlePayFee = (row) => {
    setPayingFee(row);
    setOriginalPayableAmount(Number(row.payableAmount || 0));
    setPayableAmount(Number(row.payableAmount || 0));
    setPaymentMode('cash');
    setCouponVerified(false);
    setShowPayFeeDialog(true);
  }

  const handlePayFeeSubmit = async () => {
    if (payingAmount <= 0) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please enter a valid amount' });
      return;
    }

    try {
      const payload = {
        studentId: feeDetailsStudent.id,
        studentFeeId: payingFee.id,
        feeStructureId: payingFee.feeStructureId,
        paidAmount: payingAmount,
        paymentMode: paymentMode
      };

      const res = await feesAPI.payFee(payload);
      if (res?.data?.success) {

        setPaymentHistoryId(res.data.paymentHistoryId);
        toast.current?.show({ severity: 'success', summary: 'Payment Successful', detail: res.data.message });
        setShowPayFeeDialog(false);
        setPayingFee(null);
        setPayingAmount(0);
        handleViewFeeDetails(feeDetailsStudent);
      }
    } catch (err) {
      console.error('Payment Error:', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'An error occurred during payment' });
    }
  }

  const handlePrint = () => {
    if (!paymentHistoryId) return;
    window.print();
    // Clear after a delay to ensure print preview has finished
    setTimeout(() => setPaymentHistoryId(null), 500);
  };

  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <StudentFilter user={user} handleAddStudent={handleAddStudent} globalFilterValue={globalFilterValue} onGlobalFilterChange={onGlobalFilterChange}
        selectedClass={selectedClass} classes={classes} onClassFilterChange={onClassFilterChange} selectedSection={selectedSection} sections={sections}
        onSectionFilterChange={onSectionFilterChange} selectedStatus={selectedStatus} statusOptions={statusOptions}
        onStatusFilterChange={onStatusFilterChange} showDetailsView={showDetailsView} showFeeDetails={showFeeDetails} />

      <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/20 shadow-xl overflow-hidden flex flex-col flex-1 min-h-0 mt-2">
        <div className="border-b border-gray-100 p-4 flex-shrink-0">
          <div className="space-y-3">
            <div className="flex justify-between ">
              {!showFeeDetails && !showDetailsView && (totalRecords > 0 ? (
                <span className="flex items-center px-3 py-1 bg-green-50 text-green-700 rounded-full border border-green-200 text-sm">
                  <i className="pi pi-check-circle mr-2"></i>
                  {students.length} of {totalRecords} Students
                </span>
              ) : (
                <span className="flex items-center px-3 py-1 bg-orange-50 text-orange-700 rounded-full border border-orange-200 text-sm">
                  <i className="pi pi-exclamation-triangle mr-2"></i>
                  No students found
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="pi pi-user text-blue-600 text-xl"></i>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-lg font-bold text-gray-900">Loading Students</span>
                  <span className="text-sm text-gray-500 italic">Please wait while we fetch the directory...</span>
                </div>
              </div>
            </div>
          ) : showFeeDetails && feeDetailsStudent ?
            (
              <FeeDetails
                student={feeDetailsStudent} fees={studentFees} feeLoading={feeLoading}
                handleBackToStudentsTable={handleBackToStudentsTable} handlePayFee={handlePayFee}
                expandedRows={expandedRows} setExpandedRows={setExpandedRows} showingContainer={false}
              />
            ) : showDetailsView && detailsStudent ?
              (
                <StudentDetails student={detailsStudent} handleBackToTable={handleBackToTable} showingContainer={false} />
              ) : showTransportForm == true ?
                (
                  <TransportForm transportForm={transportForm} setTransportForm={setTansportForm} editingTransport={editingTransport} handleEditTransportForm = {handleEditTransportForm} handleSaveTransport={handleSaveTransport} handleCloseTransport={handleCloseTransport} slabs={slabs} />
                ) :
                (
                  <StudentTable
                    students={students} lazyParams={lazyParams} totalRecords={totalRecords}
                    loading={loading} onPage={onPage} onSort={onSort}
                    handleViewStudent={handleViewStudent} handleViewFeeDetails={handleViewFeeDetails}
                    handleEditStudent={handleEditStudent} handleDeleteStudent={handleDeleteStudent}
                    handleAddTransport={handleAddTransport}
                  />
                )}
        </div>
      </div>

      <StudentForm
        visible={showStudentForm} onHide={() => setShowStudentForm(false)}
        editingStudent={editingStudent} studentForm={studentForm} setStudentForm={setStudentForm}
        classes={classes} sections={sections} loadSections={loadSections}
        handleSaveStudent={handleSaveStudent} convertToBase64={convertToBase64} toast={toast}
      />

      <PayFeeDialog
        visible={showPayFeeDialog} onHide={() => setShowPayFeeDialog(false)}
        payingFee={payingFee} couponVerified={couponVerified}
        originalPayableAmount={originalPayableAmount} payableAmount={payableAmount}
        payingAmount={payingAmount} setPayingAmount={setPayingAmount}
        paymentMode={paymentMode} setPaymentMode={setPaymentMode}
        handlePayFeeSubmit={handlePayFeeSubmit}
      />

      <div className="printable-content hidden print:block">
        {paymentHistoryId && (
          <PaymentInvoice ref={invoiceRef} paymentHistoryId={paymentHistoryId} onDataLoaded={handlePrint}
          />
        )}
      </div>
    </div>
  );
};
export default Students;